import { useEffect, useState } from "react";
import { api, idr, detailText } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Phone, Mail, User2, StickyNote, Building2, UtensilsCrossed, Flower2, ClipboardList, Camera, Video, Sparkles, Shirt, ScrollText, Music, Mic2, Gift, Cake, Leaf, Car, Briefcase } from "lucide-react";
import { toast } from "sonner";
import NumberInput from "@/components/app/NumberInput";
import { useT } from "@/context/LanguageContext";

const CATS = ["Venue","Catering","Decoration","Wedding Organizer","Photography","Videography","Makeup Artist","Dress","Suit","Invitation","Entertainment","MC","Souvenir","Cake","Florist","Transportation"];
const BOOKING_STATUSES = ["researching","contacted","quotation","shortlisted","booked","completed"];
const PAYMENT_STATUSES = ["pending","dp_paid","partially_paid","paid"];

// Ikon per kategori agar user langsung mengenali jenis vendor
const CAT_ICONS = {
  Venue: Building2,
  Catering: UtensilsCrossed,
  Decoration: Flower2,
  "Wedding Organizer": ClipboardList,
  Photography: Camera,
  Videography: Video,
  "Makeup Artist": Sparkles,
  Dress: Shirt,
  Suit: Shirt,
  Invitation: ScrollText,
  Entertainment: Music,
  MC: Mic2,
  Souvenir: Gift,
  Cake: Cake,
  Florist: Leaf,
  Transportation: Car,
};

// Label & warna status supaya mudah dipahami sekali lihat
const BOOKING_LABELS = {
  researching: "Riset",
  contacted: "Dihubungi",
  quotation: "Quotation",
  shortlisted: "Shortlist",
  booked: "Dibooking",
  completed: "Selesai",
};
const BOOKING_STYLES = {
  researching: "bg-sky-500/10 text-sky-700 border-sky-500/30",
  contacted: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  quotation: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  shortlisted: "bg-violet-500/10 text-violet-700 border-violet-500/30",
  booked: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  completed: "bg-teal-600/15 text-teal-800 border-teal-600/40",
};
const PAYMENT_LABELS = {
  pending: "Belum bayar",
  dp_paid: "DP dibayar",
  partially_paid: "Bayar sebagian",
  paid: "Lunas",
};
const PAYMENT_STYLES = {
  pending: "bg-muted text-muted-foreground border-border",
  dp_paid: "bg-sky-500/10 text-sky-700 border-sky-500/30",
  partially_paid: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  paid: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
};

const emptyForm = { name: "", category: "Venue", contact_person: "", phone: "", email: "", price: 0, booking_status: "researching", payment_status: "pending", notes: "" };

export default function Vendors() {
  const { t } = useT();
  const [vendors, setVendors] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [addForm, setAddForm] = useState(emptyForm);
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/vendors").then(({ data }) => setVendors(data.vendors ?? [])).catch((e) => {
    toast.error(detailText(e?.response?.data?.detail) || "Gagal memuat vendor");
  });
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!addForm.name.trim()) return toast.error(t("vendors.name_required"));
    try {
      await api.post("/vendors", addForm);
      setAddOpen(false);
      setAddForm(emptyForm);
      load();
      toast.success(t("vendors.added"));
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menambah vendor");
    }
  };

  const openDetail = (v) => {
    setSelected(v);
    setEditForm({
      name: v.name || "", category: v.category || "Venue",
      contact_person: v.contact_person || "", phone: v.phone || "", email: v.email || "",
      price: v.price || 0,
      booking_status: v.booking_status || "researching",
      payment_status: v.payment_status || "pending",
      notes: v.notes || "",
    });
    setDetailOpen(true);
  };

  const saveDetail = async () => {
    if (!selected) return;
    if (!editForm.name.trim()) return toast.error(t("vendors.name_required"));
    setSaving(true);
    try {
      await api.patch(`/vendors/${selected.vendor_id}`, editForm);
      toast.success(t("vendors.updated"));
      setDetailOpen(false);
      load();
    } catch (e) { toast.error(detailText(e?.response?.data?.detail) || "Error"); }
    finally { setSaving(false); }
  };

  const removeDetail = async () => {
    if (!selected) return;
    if (!window.confirm(t("vendors.confirm_delete"))) return;
    try {
      await api.delete(`/vendors/${selected.vendor_id}`);
      setDetailOpen(false);
      load();
      toast.success(t("vendors.deleted"));
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menghapus vendor");
    }
  };

  const filtered = filter === "all" ? vendors : vendors.filter((v) => v.category === filter);

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{t("vendors.section")}</div>
          <h1 className="font-serif text-4xl">{t("vendors.title")}</h1>
          <p className="text-xs text-muted-foreground mt-1">{t("vendors.click_hint")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={filter} onValueChange={setFilter}><SelectTrigger data-testid="vendor-category-filter" className="w-48"><SelectValue placeholder={t("vendors.all_categories")} /></SelectTrigger><SelectContent><SelectItem value="all">{t("vendors.all_categories")}</SelectItem>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild><Button data-testid="vendor-add-btn" className="rounded-full"><Plus className="h-4 w-4 mr-1" /> {t("vendors.add_btn")}</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("vendors.new")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("common.name")}</Label><Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="vendor-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{t("common.category")}</Label><Select value={addForm.category} onValueChange={(v) => setAddForm({ ...addForm, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>{t("vendors.price_idr")}</Label><NumberInput prefix="Rp" value={addForm.price} onChange={(v) => setAddForm({ ...addForm, price: v })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{t("vendors.contact_person")}</Label><Input value={addForm.contact_person} onChange={(e) => setAddForm({ ...addForm, contact_person: e.target.value })} /></div>
                  <div><Label>{t("common.phone")}</Label><Input value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} /></div>
                </div>
                <Button onClick={add} className="w-full rounded-full" data-testid="save-vendor-btn">{t("vendors.save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground rounded-2xl">{t("vendors.empty")}</Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((v) => {
            const CatIcon = CAT_ICONS[v.category] || Briefcase;
            const bs = v.booking_status || "researching";
            const ps = v.payment_status || "pending";
            return (
              <button
                key={v.vendor_id}
                type="button"
                onClick={() => openDetail(v)}
                data-testid="vendor-card-item"
                className="text-left group"
              >
                <Card className="rounded-2xl overflow-hidden h-full border-border/70 bg-card/80 backdrop-blur-sm group-hover:shadow-lg group-hover:-translate-y-0.5 group-hover:border-primary/30 transition-all duration-200">
                  {/* Identitas vendor */}
                  <div className="flex items-center gap-3 p-5 pb-4">
                    <div className="h-11 w-11 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                      <CatIcon strokeWidth={1.5} className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-lg leading-snug truncate" title={v.name}>{v.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{v.category}</div>
                    </div>
                  </div>

                  <div className="px-5 pb-5 space-y-3">
                    {/* Estimasi harga */}
                    <div className="flex items-baseline justify-between gap-2 rounded-xl border border-border/60 bg-muted/40 px-3 py-2">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Estimasi</span>
                      {v.price > 0 ? (
                        <span className="font-semibold text-primary">{idr(v.price)}</span>
                      ) : (
                        <span className="text-xs italic text-muted-foreground">Belum diisi</span>
                    )}
                    </div>

                    {/* Kontak */}
                    {(v.contact_person || v.phone) && (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-0">
                        <User2 className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">
                          {v.contact_person || "-"}{v.phone && ` · ${v.phone}`}
                        </span>
                      </div>
                    )}

                    {/* Status booking & pembayaran */}
                    <div className="flex flex-wrap gap-1.5">
                      <span
                        data-testid="vendor-status-pill"
                        className={`text-[11px] font-medium px-2.5 py-1 rounded-full border whitespace-nowrap ${BOOKING_STYLES[bs] || "bg-muted text-muted-foreground border-border"}`}
                      >
                        {BOOKING_LABELS[bs] || bs}
                      </span>
                      <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full border whitespace-nowrap ${PAYMENT_STYLES[ps] || PAYMENT_STYLES.pending}`}>
                        {PAYMENT_LABELS[ps] || ps}
                      </span>
                    </div>
                  </div>
                </Card>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail / Edit Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg" data-testid="vendor-detail-modal">
          <DialogHeader><DialogTitle>{t("vendors.details")}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div><Label>{t("common.name")}</Label><Input data-testid="vendor-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("common.category")}</Label>
                  <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("vendors.price_idr")}</Label><NumberInput prefix="Rp" value={editForm.price} onChange={(v) => setEditForm({ ...editForm, price: v })} data-testid="vendor-edit-price" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("vendors.booking_status")}</Label>
                  <Select value={editForm.booking_status} onValueChange={(v) => setEditForm({ ...editForm, booking_status: v })}>
                    <SelectTrigger data-testid="vendor-edit-booking"><SelectValue /></SelectTrigger>
                    <SelectContent>{BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s}>{BOOKING_LABELS[s] || s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("vendors.payment_status")}</Label>
                  <Select value={editForm.payment_status} onValueChange={(v) => setEditForm({ ...editForm, payment_status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{PAYMENT_LABELS[s] || s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="flex items-center gap-1"><User2 className="h-3.5 w-3.5" /> {t("vendors.contact_person")}</Label><Input value={editForm.contact_person} onChange={(e) => setEditForm({ ...editForm, contact_person: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {t("common.phone")}</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
                <div><Label className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {t("common.email")}</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
              </div>
              <div><Label className="flex items-center gap-1"><StickyNote className="h-3.5 w-3.5" /> {t("common.notes")}</Label><Textarea rows={3} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter className="flex-row justify-between gap-2">
            <Button variant="ghost" onClick={removeDetail} className="text-destructive" data-testid="vendor-detail-delete"><Trash2 className="h-4 w-4 mr-1" /> {t("common.delete")}</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDetailOpen(false)}>{t("common.cancel")}</Button>
              <Button onClick={saveDetail} disabled={saving} data-testid="vendor-detail-save">{saving ? t("common.saving") : t("vendors.save_changes")}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
